<template>
  <v-card class="mt-3">
    <v-card-title class="headline">
      <v-icon left>compare_arrows</v-icon>
      Rakip Linkleri
      <v-spacer></v-spacer>
      <v-btn small color="primary" :loading="refreshing" :disabled="!links.length" @click="refresh">
        <v-icon left small>refresh</v-icon>
        Bu ürünü şimdi çek
      </v-btn>
    </v-card-title>
    <v-card-text>
      <div class="caption grey--text mb-3">
        Bu ürünün pazaryerlerindeki birebir rakiplerinin linklerini ekleyin. Fiyat, satıcı ve stok otomatik olarak
        periyodik çekilir (Admin &gt; Rakip Fiyatları). Fark, bizim aynı pazaryerindeki fiyatımıza göredir;
        eksi değer rakibin bizden ucuz olduğunu gösterir.
      </div>

      <v-alert :value="!!error" type="error" outline dismissible @input="error = null">{{ error }}</v-alert>

      <div v-for="site in sites" :key="site.key" class="mb-4">
        <div class="subheading font-weight-medium mb-1">
          {{ site.name }}
          <span class="caption grey--text">({{ linksOf(site.key).length }})</span>
        </div>

        <table v-if="linksOf(site.key).length" class="competitor-table">
          <thead>
            <tr>
              <th class="text-xs-left">Satıcı / Ürün</th>
              <th class="text-xs-right">Rakip Fiyat</th>
              <th class="text-xs-right">Bizim Fiyat</th>
              <th class="text-xs-right">Fark</th>
              <th>Stok</th>
              <th>Durum</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="l in linksOf(site.key)" :key="l.id">
              <td class="text-xs-left">
                <a :href="l.url" target="_blank" rel="noopener">{{ l.sellerName || "Satıcı bilinmiyor" }}</a>
                <div class="caption grey--text product-name">{{ l.productName || l.url }}</div>
              </td>
              <td class="text-xs-right">
                <a v-if="l.price != null" href="#" @click.prevent="openHistory(l)">{{ l.price | formatMoney }}</a>
                <span v-else>-</span>
                <div v-if="l.price != null && l.priceSource === 'Script'">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <span v-on="on" class="api-warn">API'den gelmedi</span>
                  </template>
                  <span>Sepet fiyatı alınamadı, sayfa fiyatı gösteriliyor.<br />{{ l.apiError }}</span>
                </v-tooltip>
                </div>
                <div v-if="l.originalPrice && l.originalPrice > l.price" class="caption grey--text strike">
                  {{ l.originalPrice | formatMoney }}
                </div>
              </td>
              <td class="text-xs-right">{{ l.ourPrice != null ? $options.filters.formatMoney(l.ourPrice) : "-" }}</td>
              <td class="text-xs-right" :class="diffClass(l)">
                <template v-if="l.diff != null">
                  {{ l.diff | formatMoney }}
                  <div class="caption">%{{ l.diffPercent }}</div>
                </template>
                <span v-else>-</span>
              </td>
              <td>
                <v-chip v-if="l.inStock === true" small color="green" text-color="white">Var</v-chip>
                <v-chip v-else-if="l.inStock === false" small color="grey" text-color="white">Yok</v-chip>
                <span v-else>-</span>
              </td>
              <td>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-chip small :color="statusColor(l.status)" text-color="white" v-on="on">{{ l.status }}</v-chip>
                  </template>
                  <span v-if="l.lastError">{{ l.lastError }}<br /></span>
                  <span>Son kontrol: {{ l.lastCheckedAt | formatDate }}</span><br />
                  <span>Son başarılı: {{ l.lastSuccessAt | formatDate }}</span>
                </v-tooltip>
              </td>
              <td class="text-xs-right">
                <v-icon small color="red" @click="remove(l)">delete</v-icon>
              </td>
            </tr>
          </tbody>
        </table>

        <v-layout align-center>
          <v-flex xs12 md9>
            <v-text-field
              v-model="newUrls[site.key]"
              :label="`${site.name} rakip linki ekle`"
              :placeholder="site.placeholder"
              prepend-icon="link"
              hide-details
              @keyup.enter="add(site)"
            ></v-text-field>
          </v-flex>
          <v-flex shrink class="pl-2">
            <v-btn small color="success" :loading="adding === site.key" @click="add(site)">
              <v-icon left small>add</v-icon>
              Ekle
            </v-btn>
          </v-flex>
        </v-layout>
      </div>
    </v-card-text>

    <v-dialog v-model="historyDialog" max-width="560px">
      <v-card>
        <v-card-title class="headline">Fiyat Geçmişi (30 gün)</v-card-title>
        <v-card-text>
          <div class="caption grey--text mb-2">
            {{ historyLink && historyLink.sellerName }} — yalnızca fiyat, stok ya da satıcı değiştiğinde kayıt düşer.
          </div>
          <v-progress-linear v-if="historyLoading" indeterminate></v-progress-linear>
          <table v-else class="competitor-table">
            <thead>
              <tr>
                <th class="text-xs-left">Tarih</th>
                <th class="text-xs-right">Fiyat</th>
                <th>Stok</th>
                <th class="text-xs-left">Satıcı</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(h, i) in history" :key="i">
                <td class="text-xs-left">{{ h.createdDate | formatDate }}</td>
                <td class="text-xs-right">{{ h.price | formatMoney }}</td>
                <td>{{ h.inStock === false ? "Yok" : "Var" }}</td>
                <td class="text-xs-left">{{ h.sellerName }}</td>
              </tr>
              <tr v-if="!history.length">
                <td colspan="4" class="grey--text">Kayıt yok.</td>
              </tr>
            </tbody>
          </table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="historyDialog = false">Kapat</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import competitorPrices from "@/api/competitorPrices";

export const COMPETITOR_SITES = [
  { key: "trendyol", name: "Trendyol", placeholder: "https://www.trendyol.com/...-p-123456" },
  { key: "hepsiburada", name: "Hepsiburada", placeholder: "https://www.hepsiburada.com/...-p-HBV..." },
  { key: "idefix", name: "İdefix", placeholder: "https://www.idefix.com/...-p-123456" },
  { key: "n11", name: "N11", placeholder: "https://www.n11.com/urun/..." }
];

const HOSTS = { "trendyol.com": "trendyol", "hepsiburada.com": "hepsiburada", "idefix.com": "idefix", "n11.com": "n11" };

export default {
  name: "CompetitorLinks",
  props: {
    productId: { type: [Number, String], required: true }
  },
  data: () => ({
    sites: COMPETITOR_SITES,
    links: [],
    newUrls: {},
    adding: null,
    refreshing: false,
    error: null,
    historyDialog: false,
    historyLink: null,
    history: [],
    historyLoading: false
  }),
  created() {
    this.load();
  },
  watch: {
    productId() {
      this.load();
    }
  },
  methods: {
    async load() {
      try {
        const { data } = await competitorPrices.listByProduct(this.productId);
        this.links = data || [];
      } catch (e) {
        this.error = this.message(e, "Rakip linkleri yüklenemedi.");
      }
    },
    linksOf(site) {
      return this.links.filter(l => l.site === site);
    },
    siteOf(url) {
      try {
        const host = new URL(url).hostname.toLowerCase().replace(/^www\./, "");
        return HOSTS[host] || null;
      } catch (e) {
        return null;
      }
    },
    async add(site) {
      const url = (this.newUrls[site.key] || "").trim();
      if (!url) return;
      const detected = this.siteOf(url);
      if (detected !== site.key) {
        this.error = detected
          ? `Bu link ${this.sites.find(s => s.key === detected).name} linki; ${site.name} alanına değil kendi alanına ekleyin.`
          : `Geçerli bir ${site.name} ürün linki girin.`;
        return;
      }
      this.adding = site.key;
      this.error = null;
      try {
        const { data } = await competitorPrices.addLink(this.productId, url);
        this.links.push(data);
        this.$set(this.newUrls, site.key, "");
        if (data.status === "Hata") this.error = `Link eklendi ama fiyat okunamadı: ${data.lastError}`;
      } catch (e) {
        this.error = this.message(e, "Link eklenemedi.");
      } finally {
        this.adding = null;
      }
    },
    async remove(link) {
      if (!confirm(`${link.sellerName || link.url} rakip linki silinsin mi?`)) return;
      try {
        await competitorPrices.deleteLink(link.id);
        this.links = this.links.filter(l => l.id !== link.id);
      } catch (e) {
        this.error = this.message(e, "Link silinemedi.");
      }
    },
    async refresh() {
      this.refreshing = true;
      this.error = null;
      try {
        const { data } = await competitorPrices.refreshProduct(this.productId);
        this.links = data || [];
      } catch (e) {
        this.error = this.message(e, "Fiyatlar çekilemedi.");
      } finally {
        this.refreshing = false;
      }
    },
    async openHistory(link) {
      this.historyLink = link;
      this.history = [];
      this.historyDialog = true;
      this.historyLoading = true;
      try {
        const { data } = await competitorPrices.history(link.id, 30);
        this.history = data || [];
      } finally {
        this.historyLoading = false;
      }
    },
    diffClass(l) {
      if (l.diff == null || l.diff === 0) return "";
      return l.diff < 0 ? "red--text font-weight-bold" : "green--text";
    },
    statusColor(status) {
      return status === "OK" ? "green" : status === "Hata" ? "red" : "grey";
    },
    message(e, fallback) {
      return (e && e.response && e.response.data && e.response.data.message) || fallback;
    }
  }
};
</script>

<style scoped>
.competitor-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 8px;
}
.competitor-table th,
.competitor-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #eee;
  vertical-align: top;
  font-size: 13px;
}
.competitor-table th {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.54);
  white-space: nowrap;
}
.product-name {
  max-width: 420px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.strike {
  text-decoration: line-through;
}
.api-warn {
  display: inline-block;
  font-size: 11px;
  color: #e65100;
  background: #fff3e0;
  border-radius: 3px;
  padding: 0 4px;
  cursor: help;
  white-space: nowrap;
}
</style>
